import { useEffect } from 'react'
import { callAPI } from '../hooks/api'
import { useState } from 'react'
import Spinner from './Spinner'
import { toast } from 'react-toastify'
import useAbility from '@/hooks/use-ability'
import UpgradeLockIconLayout from './upgrade-lock-icon-layout'
import UpgradeLayout from './upgrade-layout'

export default function Watershed({
  watershed,
  isDemoUser,
}) {
  const [loading, setLoading] = useState(false)

  const { checkAbility } = useAbility()

  const enableSubbasinInformation = checkAbility(
    'subbasin_information_name_number'
  )

  const enableHydrologicRegion = checkAbility('hydrologic_region')

  function normalizeKeys(obj) {
    if (typeof obj !== 'object' || obj === null) {
      return obj
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => normalizeKeys(item))
    }

    return Object.keys(obj).reduce((acc, key) => {
      const normalizedKey = key.toLowerCase()
      const value = obj[key]
      acc[normalizedKey] = value
      return acc
    }, {})
  }

  if (loading) {
    return <Spinner local={true} />
  }
  if (!Object.keys(watershed).length)
    return <p className="ml-[30px] ">No Watershed Data</p>
  else
    return (
      <div className="w-full">
        {watershed.watershed && (
          <>
            <h2 className="underline ml-[20px] mt-[20px]">Watershed:</h2>
            <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-[10px]">
              <li className="flex items-center whitespace-wrap">
                <p className="font-semibold ml-[30px] ">
                  <span className={`${isDemoUser ? 'blur' : ''}`}>
                    {watershed.watershed
                      ? watershed.watershed
                      : 'No Watershed Data'}
                  </span>
                </p>
              </li>
            </ul>
          </>
        )}
        {watershed.watershed_gsa && (
          <div>
            <h2 className="underline ml-[20px]">
              Groundwater Sustainability Agency:
            </h2>
            <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-[20px]">
              <li className="flex items-center whitespace-wrap relative ml-[30px] ">
                <UpgradeLockIconLayout
                  className="absolute min-w-[60px] justify-start"
                  show={false}
                />
                <p className="font-semibold">
                  <span className={`${isDemoUser ? 'blur' : ''}`}>
                    {watershed.watershed_gsa.length
                      ? watershed.watershed_gsa
                          .map((item) => normalizeKeys(item).gsa_name)
                          .join(', ')
                      : 'No GSA Data'}
                  </span>
                </p>
              </li>
            </ul>
          </div>
        )}
        {watershed.watershed_wd && (
          <>
            <h2 className="underline ml-[20px]">Water District:</h2>
            <ul className="flex items-center space-x-2 sm:space-x-10 text-sm font-semibold mb-[20px]">
              <li className="flex items-center whitespace-wrap relative ml-[30px] ">
                <UpgradeLockIconLayout
                  className="absolute min-w-[60px] justify-start"
                  show={false}
                />
                <p className="font-semibold">
                  <span className={`${isDemoUser ? 'blur' : ''}`}>
                    {watershed.watershed_wd.length
                      ? watershed.watershed_wd
                          .map((item) => normalizeKeys(item).wd_name)
                          .join(', ')
                      : 'White Area'}
                  </span>
                </p>
              </li>
            </ul>
          </>
        )}
        {watershed.watershed_hydrologic_region && (
          <div className="relative">
            <UpgradeLayout
              title="Upgrade to view Hydrologic Region."
              needUpgrade={!enableHydrologicRegion}
            />
            <h2 className="underline ml-[20px]">Hydrologic Region:</h2>
            <ul className="flex flex-col items-left text-sm font-semibold mb-[20px]">
              <li className="flex items-left whitespace-wrap">
                <p className="font-semibold ml-[30px]">
                  <span className={`${isDemoUser ? 'blur' : ''}`}>
                    {watershed.watershed_hydrologic_region
                      ? watershed.watershed_hydrologic_region.hydrologic_region
                      : 'No Hydrologic Region Data'}
                  </span>
                </p>
              </li>
              <li className="flex items-left whitespace-wrap">
                <div className="font-semibold ml-[30px] flex flex-row gap-1">
                  <p>Basin Name:</p>
                  <div className="relative">
                    <UpgradeLockIconLayout
                      className="absolute min-w-[60px]"
                      show={!enableSubbasinInformation && enableHydrologicRegion}
                    />
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {watershed.watershed_hydrologic_region
                        ? watershed.watershed_hydrologic_region.basin_name
                        : 'No Basin Name Data'}
                    </span>
                  </div>
                </div>
              </li>
              <li className="flex items-left whitespace-wrap">
                <div className="font-semibold ml-[30px] flex flex-row gap-1">
                  <p>Subbasin Name:</p>
                  <div className="relative">
                    <UpgradeLockIconLayout
                      className="absolute min-w-[60px]"
                      show={
                        !enableSubbasinInformation && enableHydrologicRegion
                      }
                    />
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {watershed.watershed_hydrologic_region
                        ? watershed.watershed_hydrologic_region.subbasin_name
                        : 'No Subbasin Name Data'}
                    </span>
                  </div>
                </div>
              </li>
              <li className="flex items-left whitespace-wrap">
                <div className="font-semibold ml-[30px] flex flex-row gap-1">
                  <p>Subbasin No:</p>
                  <div className="relative">
                    <UpgradeLockIconLayout
                      className="absolute min-w-[60px]"
                      show={
                        !enableSubbasinInformation && enableHydrologicRegion
                      }
                    />
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {watershed.watershed_hydrologic_region
                        ? watershed.watershed_hydrologic_region.subbasin_no
                        : 'No Subbasin Number Data'}
                    </span>
                  </div>
                </div>
              </li>
              {/* {watershed.watershed_gsa.length > 0 &&
                watershed.watershed_gsa.map(
                  (basin) =>
                    normalizeKeys(basin).basin_subbasin_pdf && (
                      <li
                        key={normalizeKeys(basin).basin_subbasin_number[0]}
                        className="flex items-left whitespace-wrap"
                      >
                        <a
                          href={normalizeKeys(basin).basin_subbasin_pdf[0]}
                          target="_blank"
                          className="font-semibold ml-[30px] text-cyan-900 cursor-pointer"
                        >
                          Basin Boundaries Description PDF (
                          {normalizeKeys(basin).basin_subbasin_number[0]})
                        </a>
                      </li>
                    )
                )} */}
            </ul>
          </div>
        )}
      </div>
    )
}
